/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useState, useEffect, useRef } from "react";
import { ImageSelectDialog } from "cms/images/image-selector/dialog-select";
import { ImageEditContext } from "cms/images/image-selector/context/image-edit-context";
import { IconButton, Button } from "@mui/material";
import { SVGIcons } from "cms/icons/svg-icons";
import { MODES } from "cms/images/image-selector/image-selector-config";
import { uploadFiles } from "cms/images/image-selector/dialog-select/upload/AzureStorage/upload-processor";
import { useAuth } from "cms/auth/hooks/use-auth";
import { useImageLoadedForUpload } from "cms/images/hooks/use-image-loaded";
import { useSimplicityImage } from "cms/images/hooks/use-simplicity-image";
import { Logger } from "cms/utils/log/logger";
import { isValidGuid } from "utils/empty-exists";

const ServiceListIconSelectorView = (props) => {

    // const { t } = useTranslation()
    const icon = props?.icon;
    const onChangeIcon = props?.onChangeIcon;
    const onRemoveIcon = props?.onRemoveIcon;
    
    const { token } = useAuth();
    const { saveImage } = useSimplicityImage();
    
    const iconAsObject = icon ? { url: icon } : null;
    const [selectedImage, setSelectedImage] = useState(iconAsObject);
    
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState(MODES.SELECT);
    const [selectedTab, setSelectedTab] = useState(0);
    
    // Upload state variables
    const [filesReady, setFilesReady] = useState(false);
    const [fileError, setFileError] = useState("");
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState({ 
        status: "NOT_STARTED", 
        message: "The upload has not started." 
    });
    const refs = useRef([]);
    const [statusList, setStatusList] = useState([]);
    
    // Additional state for full functionality
    const [images, setImages] = useState(); // for Gallery
    const [original, setOriginal] = useState(); // for Gallery
    const [openEdit, setOpenEdit] = useState(false);
    const [openRemove, setOpenRemove] = useState(false);
    const [openArchive, setOpenArchive] = useState(false);
    const [openRename, setOpenRename] = useState(false);
    const [openReplace, setOpenReplace] = useState(false);
    // const [hasImage, setHasImage] = useState()
    const initialLocalState = { image: { title: "", description: "" } };
    const [localState, setLocalState] = useState(initialLocalState);
    const [selectedEditTab, setSelectedEditTab] = useState(0);
    const [isArchiving, setIsArchiving] = useState(false);
    const [isReplacing, setIsReplacing] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [isFieldValid, setIsFieldValid] = useState(false);
    
    const { onLoad } = useImageLoadedForUpload(refs, files);

    // Initialize state based on icon
    useEffect(() => {
        if (!icon || !isValidGuid(icon?.guid)) {
            setMode(MODES.SELECT);
        } else {
            setOriginal(icon);
            setSelectedImage(icon);
            // setHasImage(true);
        }
    }, [icon, open]);

    const handleEditIcon = () => {
        setOpen(true);
    };
    
    const handleRemoveIcon = () => {
        onRemoveIcon();
    };

    // Image selector handlers
    const onSelectImage = (image) => {
        if (image && image.url) {
            onChangeIcon(image.url);
        }
        setOpen(false);
    };
    
    const onSaveImage = async () => {
        // is partial record as update
        const image = localState?.image;
        if (image?.guid) {
            const image_response = await saveImage(image);
            onChangeIcon(image_response.url);
        }
        handleEditClose();
        handleClose();
    };
    
    const onImageRemoved = () => {
        onRemoveIcon();
        setOpen(false);
    };
    
    const onImageDeleted = () => {
        onRemoveIcon();
        setOpen(false);
    };
    
    const onReload = () => {
        // Handle reload if needed
    };

    const handleClose = () => {
        setSelectedImage(original);
        setMode(MODES.SELECT);
        setSelectedTab(0);
        setOpen(false);
        setFileError("");
    };
    
    const handleEditClose = () => {
        setOpenEdit(false);
        setSelectedTab(0);
        setOpenEdit(false);
        setOpen(false);
        setIsArchiving(false);
        setIsReplacing(false);
        setOpenReplace(false);
    };

    const clearFiles = async () => {
        setFilesReady(false);
        setFiles([]);
        setFileError("");
    };

    const onUploadStart = async () => {
        Logger.debug("Start Image Upload...");
        await handleUpload();
    };
    
    const handleOnImageUploaded = async (uploaded_images) => {
        await onImageUploaded(uploaded_images);
        const firstImage = uploaded_images[0];
        setSelectedImage(firstImage);
    };
    
    const handleUpload = async () => {
        Logger.debug("[icon-selector-view] handleUpload");
        await uploadFiles(
            setUploading,
            statusList,
            setUploadStatus,
            files,
            handleOnImageUploaded,
            token,
        );
    };
    
    const onImageUploaded = async (results) => {
        Logger.debug("Image Uploaded", results);
        const firstImage = results[0];
        setSelectedImage(firstImage);
        onChangeIcon(firstImage.url);
        setMode(MODES.EDIT);
        setSelectedEditTab(0);
        setOpenEdit(true);
        onReload();
    };
    
    const onImageReplaced = async (image) => {
        Logger.debug("Image Replaced", image);
        setSelectedImage(image);
        onChangeIcon(image.url);
        setMode(MODES.EDIT);
        setOpenReplace(false);
        setOpenEdit(false);
        onReload(image);
    };
    
    const onImageArchived = async () => {
        Logger.debug("onImageArchived");
        setSelectedImage(undefined);
        onChangeIcon(undefined);
        setOpenArchive(false);
        setOpenEdit(false);
        setHasChanges(true);
        onReload();
    };

    const handleTabsChange = (event, newValue) => {
        switch (newValue) {
            case 0:
                setSelectedTab(newValue);
                break;
            case 1:
                setSelectedTab(newValue);
                break;
            default:
                break;
        }
    };

    // Create a complete image edit context for the dialog
    const imageEditContext = {
        open,
        setOpen,
        isArchiving,
        setIsArchiving,
        isReplacing,
        setIsReplacing,
        hasChanges,
        setHasChanges,
        openEdit,
        setOpenEdit,
        openReplace,
        setOpenReplace,
        openRemove,
        setOpenRemove,
        openArchive,
        setOpenArchive,
        openRename,
        setOpenRename,
        handleClose,
        handleEditClose,
        selectedImage,
        setSelectedImage,
        mode,
        setMode,
        onSelectImage,
        onSaveImage,
        onImageRemoved,
        onImageDeleted,
        onImageUploaded,
        onImageReplaced,
        onImageArchived,
        clearFiles,
        onUploadStart,
        filesReady,
        setFilesReady,
        selectedTab,
        setSelectedTab,
        selectedEditTab,
        setSelectedEditTab,
        // Additional required properties
        image: selectedImage,
        setImage: setSelectedImage,
        hasImage: !!selectedImage,
        onChange: handleEditIcon,
        onEdit: handleEditIcon,
        onRemove: handleRemoveIcon,
        isFieldValid,
        setIsFieldValid,
        MODES,
        startingMode: MODES.SELECT,
        startingEditTab: 0,
        EDIT_TABS: {
            PROPERTIES: "properties",
            PLACEMENTS: "placements",
        },
        handleTabsChange,
        showSave: false,
        images,
        setImages,
        localState,
        setLocalState,
        uploadFiles,
        fileError,
        setFileError,
        uploading,
        setUploading,
        uploadStatus,
        setUploadStatus,
        files,
        refs,
        setFiles,
        statusList,
        setStatusList,
        onLoad,
        debug: false,
    };

    return (
        <Fragment>
            <div className='icon-link-selector-container'>

                {/* icon empty */}
                {!icon &&
                    <div className='icon-link-selector-icon-empty'>
                        <Button
                            variant='text'
                            onClick={handleEditIcon}
                        >
                            Select Icon
                        </Button>
                    </div>
                }

                {icon &&
                    <>
                        <img src={icon} alt={icon} style={{ width: "50px", height: "50px" }} />
                    </>
                }
            </div>

            {/* icon edit delete buttons */}
            <div className='icon-link-selector-icon-buttons'>
                {icon &&
                    <>
                        <IconButton
                            size='small'
                            title={"Edit"}
                            edge='end'
                            onClick={handleEditIcon}
                        >
                            <SVGIcons.common.Edit style={{ cursor: "pointer" }} />
                        </IconButton>
                        <IconButton
                            size='small'
                            title={"Edit"}
                            edge='end'
                            onClick={handleRemoveIcon}
                        >
                            <SVGIcons.common.Delete style={{ cursor: "pointer" }} />
                        </IconButton>
                    </>
                }
            </div>

            <ImageEditContext.Provider value={imageEditContext}>
                <ImageSelectDialog />
                {/*<ImageSelector />*/}
            </ImageEditContext.Provider>
        </Fragment>
    );
};

export { ServiceListIconSelectorView as IconSelectorView };
